/**
 * CashCutCard: reusable card that shows sales by payment method
 * (cash, debit, credit, amex, transfer, mixed) with totals + transaction counts.
 *
 * Used for daily (corte de día) and monthly reports.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { formatCurrency, paymentMethodLabels } from '../lib/utils';
import { Banknote, CreditCard, ArrowLeftRight, DollarSign } from 'lucide-react';

const ICON_BY_METHOD = {
  cash: Banknote,
  debit: CreditCard,
  credit: CreditCard,
  amex: CreditCard,
  transfer: ArrowLeftRight,
  card: CreditCard,
  mixed: ArrowLeftRight,
};

const ACCENT_BY_METHOD = {
  cash: 'text-emerald-700 bg-emerald-50',
  debit: 'text-blue-700 bg-blue-50',
  credit: 'text-indigo-700 bg-indigo-50',
  amex: 'text-amber-700 bg-amber-50',
  transfer: 'text-purple-700 bg-purple-50',
  card: 'text-slate-700 bg-slate-100',
  mixed: 'text-rose-700 bg-rose-50',
};

const METHOD_ORDER = ['cash', 'card', 'transfer', 'mixed'];

export default function CashCutCard({ title, description, report, testId = 'cash-cut' }) {
  // Normalize: ensure all known methods appear, even with 0
  const reportMap = new Map(
    (report?.by_payment_method || []).map((p) => [p.method, p])
  );
  const rows = METHOD_ORDER.map((method) => ({
    method,
    total: reportMap.get(method)?.total || 0,
    count: reportMap.get(method)?.count || 0,
  })).filter((r) => METHOD_ORDER.includes(r.method));

  const total = report?.summary?.total_sales || 0;
  const txns = report?.summary?.total_transactions || 0;
  const avg = report?.summary?.average_ticket || 0;

  return (
    <Card className="border-slate-200 bg-white" data-testid={testId}>
      <CardHeader>
        <CardTitle style={{ fontFamily: 'Manrope' }}>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-xs text-slate-500 mb-1">Ingreso total</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{formatCurrency(total)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-xs text-slate-500 mb-1">Transacciones</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{txns}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-xs text-slate-500 mb-1">Ticket promedio</p>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{formatCurrency(avg)}</p>
          </div>
        </div>

        {/* By method */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-3 mb-1">
            Por método de pago
          </p>
          {rows.map(({ method, total: mTotal, count }) => {
            const Icon = ICON_BY_METHOD[method] || DollarSign;
            return (
              <div
                key={method}
                className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2"
                data-testid={`${testId}-row-${method}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-md flex items-center justify-center ${ACCENT_BY_METHOD[method] || 'bg-slate-100'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{paymentMethodLabels[method] || method}</p>
                    <p className="text-xs text-slate-400">{count} {count === 1 ? 'transacción' : 'transacciones'}</p>
                  </div>
                </div>
                <span className="font-bold text-slate-900">{formatCurrency(mTotal)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
